import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { availCategories, handleSaveDataToS3, removeDatainS3, createNewProduct, publishListing, getProductListing, updateProductListing } from "../../../API_Handler";
import PrimaryDetails from "./AddListingComponents/PrimaryDetails";
import SecondaryDetails from "./AddListingComponents/SecondaryDetails";
import TertiaryDetails from "./AddListingComponents/TertiaryDetails";
import { useToast } from "../../../../Common/ToastContext";
import { useUser } from '../../../../UserContext';

// Utility functions
const extractVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
};

// Main Component
const AddListing = ({ editMode, editProductId, setShowEditListing, setShowAddListing }) => {
    const showToast = useToast();
    const navigate = useNavigate();
    const { userListings, setUserListings } = useUser();

    //Will be used while editing a product
    const [productId, setProductID] = useState(null);
    const [isProductPublished, setIsProductPublished] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isPublishingProduct, setIsPublishingProduct] = useState(false);

    const [listingPublishedModal, setListingPublishedModal] = useState(false);
    const [listingPublishedModalContent, setListingPublishedModalContent] = useState(null);

    const [newProduct, setNewProduct] = useState({
        productName: '',
        logoUrl: '',
        bannerUrl: '',
        websiteUrl: '',
        productDescription: '',
        productFeatures: '',
        images: '',
        videos: '',
        categories: '',
        email: '',
        X: '',
        Instagram: '',
        Linkedin: '',
        Facebook: '',
        freeTrial: '',
        freeVersion: '',
        pricingUrl: '',
    });
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [logo, setLogo] = useState(null);
    const [banner, setBanner] = useState(null);
    const [imageLinks, setImageLinks] = useState([]);
    const [imagesUploading, setImagesUploading] = useState(false);
    const [videoLinks, setVideoLinks] = useState(['']);
    const [screenLoading, setLoading] = useState(false);
    const lastVideoLinkRef = useRef(null);
    const [shouldValidatePrimary, setShouldValidatePrimary] = useState(false); // Toggle validation from parent

    const handleCategoryToggle = (category) => {
        setSelectedCategories((prev) =>
            prev.includes(category.number) ? prev.filter(c => c !== category.number) : [...prev, category.number]
        );
        setNewProduct((prev) => ({ ...prev, categories: selectedCategories }));
    };

    const allowedExtensions = ['jpg', 'jpeg', 'png'];

    const handleSingleImageUpload = async (e, setFile, type) => {
        const file = e.target.files[0];
        const fileExtension = file.type.split('/').pop().toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
            alert('Invalid file type');
        } else {
            const url = await handleSaveFileToS3(file, type);
            if (type === 'logo') {
                setLogo(url);
            } else if (type === 'banner') {
                setBanner(url);
            }
        }
    };

    // Usage for Logo Upload
    const handleLogoUpload = (e) => {
        handleSingleImageUpload(e, setLogo, 'logo');
    };

    // Usage for Banner Upload
    const handleBannerUpload = (e) => {
        handleSingleImageUpload(e, setBanner, 'banner');
    };

    // Image upload handlers for product images
    const handleImageUpload = async (e) => {
        setImagesUploading(true); // Show loading indicator while images are being uploaded
        const files = Array.from(e.target.files); // Convert files to an array
        const newImageLinks = [];

        for (const file of files) {
            const fileExtension = file.type.split('/').pop().toLowerCase();
            if (!allowedExtensions.includes(fileExtension)) {
                alert('Invalid file type');
            } else {
                const url = await handleSaveFileToS3(file, 'product-image');
                if (url) {
                    newImageLinks.push(url);
                }
            }
        }
        setImageLinks((prev) => [...prev, ...newImageLinks]);
        setImagesUploading(false);

    };

    const removeLogo = async () => {
        // const res = await removeDatainS3(logo, showToast);
        setLogo(null);
        setNewProduct(prev => ({ ...prev, logoUrl: '' }));
    }

    const removeBanner = async () => {
        // const res = await removeDatainS3(banner, showToast);
        setBanner(null);
        setNewProduct(prev => ({ ...prev, bannerUrl: '' }));
    };

    const handleRemoveImage = async (index) => {
        try {
            if (imageLinks[index]) {
                // Remove the image from S3
                // await removeDatainS3(imageLinks[index], showToast);
            }
            setImageLinks((prev) => prev.filter((_, i) => i !== index));
        } catch (error) {
            console.error('Error removing image from S3:', error);
        }
    };

    // Generalized function to handle saving a single file (logo, banner, or product images) to S3
    const handleSaveFileToS3 = async (file, fileType) => {
        try {
            const fileExtension = file.type.split('/').pop();
            const res = await handleSaveDataToS3(file);
            return res;
        } catch (error) {
            console.error(`Error uploading ${fileType}:`, error);
            return null; // Return null if there's an error
        }
    };

    async function retrieveProduct(productID) {
        try {
            setLoading(true); // Show loading indicator while data is being fetched

            // Fetch the product details
            const product = await getProductListing(productID, showToast);

            setNewProduct({
                productName: product.name || '',
                logoUrl: product.thumbnail || '',
                bannerUrl: product.banner || '',
                websiteUrl: product.website || '',
                productDescription: product.description || '',
                productFeatures: product.features || '',
                videos: product.videos || [],
                categories: selectedCategories || [], // Assume categories are being handled separately
                email: product.contact_email || '',
                X: product.twitter || '',
                Instagram: product.instagram || '',
                Linkedin: product.linkedin || '',
                Facebook: product.facebook || '',
                freeTrial: product.has_free_trial ? 'true' : 'false',
                freeVersion: product.has_free_version ? 'true' : 'false',
                pricingUrl: product.pricing || '',
                categories: product.categories
            });

            if (product.is_published) {
                setIsProductPublished(true);
            }
            // // Set the individual image states
            if (product.thumbnail) {
                setLogo(product.thumbnail);
            }

            // Convert banner URL to File and set it as the banner state
            if (product.banner) {
                setBanner(product.banner);
            }

            if (product.images) {
                let imagesArray = product.images.split(',').map(url => url.trim());
                if (imagesArray.length === 1 && imagesArray[0] === "") {
                    imagesArray = [];
                }
                setImageLinks(imagesArray); // Set image links for the product
            }

            if (product.videos) {
                // Split the string by commas and trim any extra spaces
                let videosArray = product.videos.split(',').map(url => url.trim());
                if (videosArray.length > 0 && videosArray[0] === "") {
                    videosArray = [];
                }
                setVideoLinks(videosArray); // Assuming videos are comma-separated
            }


            if (product.categories) {
                const categoryIds = product.categories.map(category => category.id);
                setSelectedCategories(categoryIds);
            }


            setLoading(false); // Hide loading indicator when done
        } catch (error) {
            console.error("Error retrieving product:", error);
            setLoading(false); // Hide loading indicator in case of error
        }
    }

    // This gets called when in edit mode
    useEffect(() => {
        if (editMode) {
            setProductID(editProductId);
            retrieveProduct(editProductId);
        }
    }, [editMode]);

    const sanitizeProduct = (newProduct) => {
        let tempProduct = newProduct;
        tempProduct.images = imageLinks || "";
        tempProduct.logoUrl = logo || "";
        tempProduct.bannerUrl = banner || "";
        if (!selectedCategories || selectedCategories.length === 0) {
            showToast("Please select at least one category before saving.", "error");
            return null;
        }
        tempProduct.categories = selectedCategories;
        tempProduct.images = imageLinks.join(',');
        tempProduct.videos = videoLinks.join(',');
        setNewProduct(tempProduct);
        return true;
    }

    // Save as Draft function
    async function saveAsDraft() {
        setShouldValidatePrimary(true);
        setIsSavingDraft(true);

        const sanitizeResponse = sanitizeProduct(newProduct);
        if (!sanitizeResponse) {
            setIsSavingDraft(false);
            return;
        }

        let response = null;
        //The following code is run when the user is in edit mode
        if (editMode) {
            response = await updateProductListing(newProduct, productId, showToast, true);
            if (!response) {
                setIsSavingDraft(false);
                return;
            }
        }
        else {
            response = await createNewProduct(newProduct, showToast);
            if (!response) {
                setIsSavingDraft(false);
                return;
            }
        }

        // Update or add the new product
        const updatedListings = userListings.some((item) => item.id === response.id)
            ? userListings.map((item) => (item.id === response.id ? response : item)) // Update existing product
            : [...userListings, response]; // Add new product if not found

        setIsSavingDraft(false);
        setUserListings(updatedListings);
        setShowEditListing(false);
        setShowAddListing(false);
    }

    // Publish Product function
    async function publishProduct() {
        setShouldValidatePrimary(true);
        setIsPublishingProduct(true);

        const sanitizeResponse = sanitizeProduct(newProduct);
        if (!sanitizeResponse) {
            setIsPublishingProduct(false);
            return;
        }

        let response = null;
        //The following code is run when the user is in edit mode
        if (editMode) {
            response = await updateProductListing(newProduct, productId, showToast);
            if (!response) {
                setIsPublishingProduct(false);
                return;
            }
        }
        else {
            response = await createNewProduct(newProduct, showToast);
            if (!response) {
                setIsPublishingProduct(false);
                return;
            }
        }

        const pub = await publishListing(response.id, showToast);
        setIsPublishingProduct(false);
        // Let's update the listings with the edited / new product
        if (!pub) return;
        // Update or add the new product
        const updatedListings = userListings.some((item) => item.id === pub.id)
            ? userListings.map((item) => (item.id === pub.id ? pub : item)) // Update existing product
            : [...userListings, pub]; // Add new product if not found

        // THIS IS THE MODAL THAT WILL POP UP WHEN THE PRODUCT IS PUBLISHED
        setListingPublishedModal(true);
        setListingPublishedModalContent(pub.id);
        setUserListings(updatedListings);
        //Once product is published, lets go back to the listings page
        //These have been set in the modal
        // setShowEditListing(false);
        // setShowAddListing(false);
    }

    // Video Handlers
    const handleVideoLinkChange = (index, value) => {
        const newVideoLinks = [...videoLinks];
        newVideoLinks[index] = value;
        setVideoLinks(newVideoLinks);
        setNewProduct((prev) => ({ ...prev, videos: newVideoLinks.join(',') }));
    };

    const handleAddVideoLink = () => setVideoLinks([...videoLinks, '']);
    const handleDeleteVideoLink = (index) => setVideoLinks(videoLinks.filter((_, i) => i !== index));


    return (
        <div className="w-full flex flex-col">
            {screenLoading ? (
                <div className="flex items-center justify-center h-screen">
                    <div className="spinner-border text-black" role="status">
                    </div>
                </div>
            ) :
                (
                    <>
                        {
                            listingPublishedModal ? (
                                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                    <div className="w-[600px] flex flex-col bg-white p-6 shadow-lg text-center">
                                        <div className="w-full flex justify-end">
                                            <button className="w-[30px] h-[30px] text-[#505050] hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full flex items-center justify-center font-proximanova text-[18px] rounded-full mb-0" onClick={() => setListingPublishedModal(false)}>X</button>
                                        </div>
                                        <div className="text-[32px] font-allroundgothic text-charmHover mb-0" style={{ lineHeight: '1.2' }}>Your Listing Is Now Live!</div>
                                        <p className="mt-[16px] font-proximanova text-[18px] text-[#505050] mb-0" style={{ lineHeight: '1.2' }}>
                                            You can view your listing at:
                                        </p>
                                        <div className="mt-[16px] w-fit mx-auto border border-gray-300 rounded-full inline-flex items-center px-4 py-2">
                                            <a
                                                href={`https://www.charmhealthhub.com/explore/${listingPublishedModalContent}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={(e) => {
                                                    // e.preventDefault();
                                                    navigator.clipboard.writeText(`https://www.charmhealthhub.com/explore/${listingPublishedModalContent}`);
                                                }}
                                                className="text-charmHover flex-shrink-0 mr-2 truncate"
                                                style={{ maxWidth: '500px' }} // optional, if you need truncation
                                            >
                                                www.charmhealthhub.com/explore/{listingPublishedModalContent}
                                            </a>

                                            {/* Copy icon */}
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigator.clipboard.writeText(`https://www.charmhealthhub.com/explore/${listingPublishedModalContent}`);
                                                    showToast('Link copied to clipboard', `https://www.charmhealthhub.com/explore/${listingPublishedModalContent}`, 'success');
                                                }}
                                                className="text-charmHover mx-2"
                                                title="Copy link"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-copy" viewBox="0 0 16 16">
                                                    <path fillRule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z" />
                                                </svg>
                                            </button>

                                            {/* Redirect icon */}
                                            <a
                                                href={`https://www.charmhealthhub.com/explore/${listingPublishedModalContent}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-charmHover mx-2"
                                                title="Open in new tab"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-box-arrow-up-right" viewBox="0 0 16 16">
                                                    <path fillRule="evenodd" d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5" />
                                                    <path fillRule="evenodd" d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0z" />
                                                </svg>
                                            </a>
                                        </div>

                                        <div className="mt-[16px] text-center">
                                            <button className="text-white text-[18px] font-proximanova px-[24px] py-[3px] bg-link hover:text-integrationRequestTextHover hover:bg-charmHover w-fit rounded-full"
                                                onClick={() => {
                                                    setShowEditListing(false);
                                                    setShowAddListing(false);
                                                }}>
                                                Return to Listings
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : null
                        }
                        <div className="w-full mt-[20px] p-[16px] bg-white">
                            <PrimaryDetails
                                newProduct={newProduct}
                                setNewProduct={setNewProduct}
                                selectedCategories={selectedCategories}
                                availCategories={availCategories}
                                handleCategoryToggle={handleCategoryToggle}
                                shouldValidatePrimary={shouldValidatePrimary} // Pass validation state to child
                                setShouldValidatePrimary={setShouldValidatePrimary} // Optionally allow resetting validation flag
                            />
                            <SecondaryDetails
                                newProduct={newProduct}
                                setNewProduct={setNewProduct}
                                handleFreeTrialChange={(value) => setNewProduct({ ...newProduct, freeTrial: value })}
                                handleFreeVersionChange={(value) => setNewProduct({ ...newProduct, freeVersion: value })}
                                handlePricingUrlChange={(value) => setNewProduct({ ...newProduct, pricingUrl: value })}
                            />
                            <TertiaryDetails
                                logo={logo}
                                banner={banner}
                                imageLinks={imageLinks}
                                videoLinks={videoLinks}
                                handleLogoUpload={handleLogoUpload}
                                handleBannerUpload={handleBannerUpload}
                                handleImageUpload={handleImageUpload}
                                removeLogo={removeLogo}
                                removeBanner={removeBanner}
                                handleRemoveImage={handleRemoveImage}
                                handleVideoLinkChange={handleVideoLinkChange}
                                handleDeleteVideoLink={handleDeleteVideoLink}
                                handleAddVideoLink={handleAddVideoLink}
                                extractVideoId={extractVideoId}
                                lastVideoLinkRef={lastVideoLinkRef}
                                imagesUploading={imagesUploading}
                            />

                            <div className="w-full flex flex-row justify-end gap-[16px] my-2">
                                {isProductPublished ? null : (
                                    <button
                                        className="w-full sm:w-fit text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4"
                                        onClick={saveAsDraft}
                                        disabled={isSavingDraft} // Disable the button while saving
                                    >
                                        {isSavingDraft ? (
                                            <div className="d-flex align-items-center">
                                                <div className="spinner-border spinner-border-sm text-light mr-2" role="status">
                                                    <span className="sr-only">Saving...</span>
                                                </div>
                                                <span>Saving Draft...</span>
                                            </div>
                                        ) : (
                                            <span>Save As A Draft</span>
                                        )}
                                    </button>
                                )}

                                <button
                                    className="w-full sm:w-fit text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4"
                                    onClick={publishProduct}
                                    disabled={isPublishingProduct} // Disable the button while publishing
                                >
                                    {isPublishingProduct ? (
                                        <div className="d-flex align-items-center">
                                            <div className="spinner-border spinner-border-sm text-light mr-2" role="status">
                                                <span className="sr-only">Publishing...</span>
                                            </div>
                                            <span>Publishing Listing...</span>
                                        </div>
                                    ) : (
                                        <span>Publish Listing</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </>
                )}
        </div >
    );
}

export default AddListing;